/**
 * Cryptographic utilities for encrypting/decrypting API keys.
 * Uses AES-256-GCM with PBKDF2 key derivation.
 */

const PBKDF2_ITERATIONS = 100000
const KEY_LENGTH = 256
const IV_LENGTH = 12 // 96-bit IV for AES-GCM

/**
 * Check if Web Crypto API is available
 */
export function checkCryptoSupport(): boolean {
  if (!window.crypto?.subtle) {
    console.warn('Web Crypto API not available - AI features disabled')
    return false
  }
  return true
}

/**
 * Derive an encryption key from a Google User ID and random salt.
 * Uses PBKDF2 with SHA-256.
 */
export async function deriveEncryptionKey(
  googleUserId: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder()
  
  // Combine Google User ID with app identifier for domain separation
  const keyInput = `praxis-crm-v1:${googleUserId}`
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(keyInput),
    'PBKDF2',
    false,
    ['deriveKey']
  )
  
  // Create a fresh ArrayBuffer from the salt to satisfy strict TypeScript typing
  const saltBuffer = new ArrayBuffer(salt.length)
  new Uint8Array(saltBuffer).set(salt)
  
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Encrypt an API key using AES-GCM.
 * Returns base64-encoded IV + ciphertext.
 */
export async function encryptApiKey(
  apiKey: string,
  encryptionKey: CryptoKey
): Promise<string> {
  const encoder = new TextEncoder()
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    encryptionKey,
    encoder.encode(apiKey)
  )
  
  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(encrypted), iv.length)
  
  return btoa(String.fromCharCode(...combined))
}

/**
 * Decrypt an API key using AES-GCM.
 * Expects base64-encoded IV + ciphertext.
 * Throws specific error on authentication failure (corrupted/tampered data).
 */
export async function decryptApiKey(
  encryptedData: string,
  encryptionKey: CryptoKey
): Promise<string> {
  try {
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0))
    const iv = combined.slice(0, IV_LENGTH)
    const ciphertext = combined.slice(IV_LENGTH)
    
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      encryptionKey,
      ciphertext
    )
    
    return new TextDecoder().decode(decrypted)
  } catch (error) {
    // DOMException with name 'OperationError' indicates auth tag failure
    if (error instanceof DOMException && error.name === 'OperationError') {
      throw new Error('DECRYPTION_FAILED: Data corrupted or wrong encryption key')
    }
    throw error
  }
}

/**
 * Generate a non-reversible fingerprint from an API key.
 * Returns first 6 hex characters of SHA-256 hash.
 */
export async function generateKeyFingerprint(apiKey: string): Promise<string> {
  const encoder = new TextEncoder()
  const hash = await crypto.subtle.digest('SHA-256', encoder.encode(apiKey))
  const hashArray = Array.from(new Uint8Array(hash))
  return hashArray.slice(0, 3).map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Generate a random 32-byte salt for encryption.
 */
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32))
}

/**
 * Encode a Uint8Array as base64.
 */
export function saltToBase64(salt: Uint8Array): string {
  return btoa(String.fromCharCode(...salt))
}

/**
 * Decode a base64 string to Uint8Array.
 */
export function base64ToSalt(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
}
